if (!customElements.get('custom-artwork-builder')) {
  // Last step before the customer orders. On the stacked (mobile/tablet) layout
  // the sidebar sits on top of the steps, so it is collapsed until this step;
  // on desktop it stays visible throughout — see the modifier class in the CSS.
  var CAB_REVIEW_STEP = 4;

  class CustomArtworkBuilder extends HTMLElement {
    connectedCallback() {
      console.log('[custom-artwork-builder] init v4 (captcha-aware submit)');
      // Prefer our own <form>; fall back to an ancestor form in case the markup
      // is nested inside another form (browsers drop nested <form> tags).
      this.form = this.querySelector('form') || this.closest('form');

      // Both actions are 100% JS-driven. The submit button is type="button", so
      // it can never trigger a native (page-reloading) submit — the only way to
      // send the form is through submitForm(). Wire the action buttons up front,
      // before anything that could throw, so these paths are always live.
      var submitBtn = this.querySelector('[data-submit]');
      if (submitBtn) submitBtn.addEventListener('click', (e) => this.submitForm(e));
      var atcBtn = this.querySelector('[data-add-to-cart]');
      if (atcBtn) atcBtn.addEventListener('click', () => this.addToCart());

      // Safety net: also intercept native submit (e.g. Enter key in a field) and
      // disable native validation so we control the flow entirely.
      if (this.form) {
        this.form.setAttribute('novalidate', '');
        this.form.addEventListener('submit', (e) => this.submitForm(e));
      }

      // Must run before anything can focus a field inside the form — see setupCaptcha().
      this.captchaState = 'off';   // 'off' | 'pending' | 'ready'
      this.setupCaptcha();

      // Parse config defensively: a malformed/edited setting must not take the
      // whole builder down.
      try {
        var configEl = this.querySelector('script[data-cab-config]');
        this.config = configEl ? JSON.parse(configEl.textContent) : {};
      } catch (err) {
        console.error('Custom artwork builder: could not parse config', err);
        this.config = {};
      }

      this.currentStep = 1;

      // State
      this.pedalType = null;        // 'guitar' | 'bass'
      this.selectedPedal = null;    // { title, type, image, variantId, price, priceFormatted, url, size }
      this.artworkType = null;      // 'gallery' | 'custom'
      this.selectedArtwork = null;  // { title, series, image } for gallery
      this.feeVariant = null;       // { id, price, priceFormatted } resolved by pedal size

      this.stepLabels = {
        1: 'Instrument type',
        2: 'Select pedal',
        3: 'Artwork',
        4: 'Details & submit'
      };

      this.buildLightbox();
      this.bindEvents();

      // Fallback path: if the POST ever goes out as a real page-reloading submit
      // (Shopify's wrapper navigating instead of calling sendForm), the reloaded
      // page carries form.posted_successfully? and we land straight on success.
      var postedMarker = this.querySelector('[data-cab-posted]');
      var serverError = this.querySelector('[data-error]');
      if (postedMarker) {
        this.goToSuccess('success-quote');
        this.scrollIntoView({ block: 'start' });
      } else if (serverError && !serverError.hidden && serverError.textContent.trim()) {
        // Shopify reports form errors on the details step, so open there instead
        // of leaving the message behind a hidden step.
        this.showStep(4);
        this.scrollIntoView({ block: 'start' });
      }
    }

    bindEvents() {
      // Step navigation
      this.querySelectorAll('[data-next]').forEach(btn =>
        btn.addEventListener('click', () => this.nextStep())
      );
      this.querySelectorAll('[data-prev]').forEach(btn =>
        btn.addEventListener('click', () => this.prevStep())
      );

      // Step 1: Instrument type
      this.querySelectorAll('[data-type]').forEach(btn =>
        btn.addEventListener('click', () => this.selectType(btn))
      );

      // Step 2: Pedal selection (single)
      this.querySelectorAll('[data-pedal-card]').forEach(card =>
        card.addEventListener('click', () => this.selectPedal(card))
      );

      // Step 3: Artwork path choice
      this.querySelectorAll('[data-artwork-path]').forEach(btn =>
        btn.addEventListener('click', () => this.selectArtworkPath(btn.dataset.artworkPath))
      );

      // Step 3: Artwork back
      this.querySelectorAll('[data-artwork-back]').forEach(btn =>
        btn.addEventListener('click', () => this.showArtworkChoice())
      );

      // Step 3: Artwork filters
      this.querySelectorAll('[data-aw-filter]').forEach(btn =>
        btn.addEventListener('click', () => this.filterArtworks(btn))
      );

      // Step 3: Artwork card selection
      this.querySelectorAll('[data-aw-card]').forEach(card =>
        card.addEventListener('click', () => this.selectArtwork(card))
      );

      // Step 3: Custom vision textarea — update Next on input
      var visionTextarea = this.querySelector('[data-custom-vision]');
      if (visionTextarea) {
        visionTextarea.addEventListener('input', () => this.updateNextButton());
      }

      // Image zoom (magnifier → lightbox). These sit inside the card buttons,
      // so stop propagation to avoid selecting the card when zooming.
      this.querySelectorAll('[data-zoom]').forEach(el => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openLightbox(el.dataset.zoomSrc, el.dataset.zoomTitle);
        });
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            this.openLightbox(el.dataset.zoomSrc, el.dataset.zoomTitle);
          }
        });
      });

      // Add to Cart (gallery path) and form submit (custom path) are bound in
      // connectedCallback — before config parsing — so submission is always
      // JS-controlled and can never fall back to a native, page-reloading submit.
    }

    // ---- Step navigation ----

    showStep(step) {
      this.currentStep = step;

      var sidebar = this.querySelector('[data-sidebar]');
      if (sidebar) {
        sidebar.classList.toggle(
          'cab__sidebar--collapsed-until-review',
          step !== CAB_REVIEW_STEP
        );
      }

      this.querySelectorAll('[data-step]').forEach(el => {
        el.classList.toggle('cab__step--active', el.dataset.step === String(step));
      });

      this.querySelectorAll('[data-step-dot]').forEach(dot => {
        var dotStep = parseInt(dot.dataset.stepDot);
        dot.classList.toggle('cab__step-dot--active', dotStep === step);
        dot.classList.toggle('cab__step-dot--completed', dotStep < step);
      });
      this.querySelectorAll('[data-step-line]').forEach(line => {
        var lineStep = parseInt(line.dataset.stepLine);
        line.classList.toggle('cab__step-line--active', lineStep < step);
      });

      var label = this.querySelector('[data-step-label]');
      if (label && this.stepLabels[step]) {
        label.textContent = this.stepLabels[step];
      } else if (label) {
        label.textContent = '';
      }

      window.scrollTo({ top: this.offsetTop - 100, behavior: 'smooth' });
    }

    nextStep() {
      if (!this.validateCurrentStep()) return;
      // Entering step 4 — configure it for the chosen path
      if (this.currentStep === 3) this.prepareSubmitStep();
      this.showStep(this.currentStep + 1);
    }

    prevStep() {
      if (this.currentStep > 1) {
        this.showStep(this.currentStep - 1);
      }
    }

    updateNextButton() {
      var stepEl = this.querySelector('[data-step="' + this.currentStep + '"]');
      if (!stepEl) return;
      var nextBtn = stepEl.querySelector('[data-next]');
      if (!nextBtn) return;
      nextBtn.disabled = !this.validateCurrentStep();
    }

    // ---- Step 1: Instrument type ----

    selectType(btn) {
      this.pedalType = btn.dataset.type;

      this.querySelectorAll('[data-type]').forEach(c =>
        c.classList.toggle('cab__type-card--selected', c === btn)
      );

      // Show correct pedal grid, reset pedal selection
      this.querySelectorAll('[data-pedals-grid]').forEach(grid => {
        grid.style.display = grid.dataset.pedalsGrid === this.pedalType ? '' : 'none';
      });
      this.selectedPedal = null;
      this.feeVariant = null;
      this.querySelectorAll('[data-pedal-card]').forEach(c =>
        c.classList.remove('cab__pedal-card--selected')
      );

      this.updateNextButton();
      this.updateSidebar();
    }

    // ---- Step 2: Pedal (single select) ----

    selectPedal(card) {
      var grid = card.closest('[data-pedals-grid]');
      if (grid && grid.style.display === 'none') return;

      this.querySelectorAll('[data-pedal-card]').forEach(c =>
        c.classList.toggle('cab__pedal-card--selected', c === card)
      );

      this.selectedPedal = {
        title: card.dataset.pedalTitle,
        type: card.dataset.pedalType,
        image: card.dataset.pedalImage,
        variantId: parseInt(card.dataset.pedalVariantId) || 0,
        price: parseInt(card.dataset.pedalPrice) || 0,
        priceFormatted: card.dataset.pedalPriceFormatted || this.formatMoney(0),
        url: card.dataset.pedalUrl,
        size: card.dataset.pedalSize || ''
      };

      this.resolveFeeVariant();
      this.updateChoicePrice();
      this.updatePriceSummary();
      this.updateNextButton();
      this.updateSidebar();
    }

    // ---- Fee resolution by pedal size ----

    resolveFeeVariant() {
      if (!this.selectedPedal) {
        this.feeVariant = null;
        return;
      }
      var size = this.selectedPedal.size;
      var match = size && this.config.feeVariants ? this.config.feeVariants[size] : null;
      this.feeVariant = match || this.config.feeVariantFallback || null;
    }

    // ---- Gallery path-choice price (reflects the selected pedal's size) ----

    updateChoicePrice() {
      var el = this.querySelector('[data-choice-gallery-price]');
      if (!el) return;
      el.textContent = this.feeVariant && this.feeVariant.priceFormatted
        ? 'Custom artwork fee: ' + this.feeVariant.priceFormatted
        : '';
    }

    // ---- Step 3: Artwork ----

    selectArtworkPath(path) {
      this.artworkType = path;

      this.querySelectorAll('[data-artwork-path]').forEach(c =>
        c.classList.toggle('cab__type-card--selected', c.dataset.artworkPath === path)
      );

      this.querySelector('[data-artwork-choice]').style.display = 'none';
      this.querySelectorAll('[data-artwork-screen]').forEach(el => {
        el.style.display = el.dataset.artworkScreen === path ? '' : 'none';
      });

      if (path === 'custom') {
        this.selectedArtwork = null;
        this.querySelectorAll('[data-aw-card]').forEach(c =>
          c.classList.remove('cab__artwork-card--selected')
        );
      }

      this.updatePriceSummary();
      this.updateNextButton();
      this.updateSidebar();
    }

    showArtworkChoice() {
      this.artworkType = null;
      this.selectedArtwork = null;

      this.querySelector('[data-artwork-choice]').style.display = '';
      this.querySelectorAll('[data-artwork-screen]').forEach(el => {
        el.style.display = 'none';
      });
      this.querySelectorAll('[data-artwork-path]').forEach(c =>
        c.classList.remove('cab__type-card--selected')
      );
      this.querySelectorAll('[data-aw-card]').forEach(c =>
        c.classList.remove('cab__artwork-card--selected')
      );

      this.updateNextButton();
      this.updateSidebar();
    }

    filterArtworks(activeBtn) {
      var filter = activeBtn.dataset.awFilter;

      this.querySelectorAll('[data-aw-filter]').forEach(btn =>
        btn.classList.toggle('cab__filter--active', btn === activeBtn)
      );

      this.querySelectorAll('[data-aw-card]').forEach(card => {
        card.style.display = (filter === 'all' || card.dataset.awSeries === filter) ? '' : 'none';
      });
    }

    selectArtwork(card) {
      this.querySelectorAll('[data-aw-card]').forEach(c =>
        c.classList.remove('cab__artwork-card--selected')
      );
      card.classList.add('cab__artwork-card--selected');

      this.selectedArtwork = {
        title: card.dataset.awTitle,
        series: card.dataset.awSeriesLabel,
        image: card.dataset.awImage
      };

      this.updatePriceSummary();
      this.updateNextButton();
      this.updateSidebar();
    }

    // ---- Validation ----

    validateCurrentStep() {
      switch (this.currentStep) {
        case 1:
          return this.pedalType !== null;
        case 2:
          return this.selectedPedal !== null;
        case 3:
          if (this.artworkType === 'gallery') return this.selectedArtwork !== null;
          if (this.artworkType === 'custom') {
            var vision = this.querySelector('[data-custom-vision]');
            return vision && vision.value.trim().length > 0;
          }
          return false;
        default:
          return true;
      }
    }

    // ---- Price summary (gallery, step 3) ----

    updatePriceSummary() {
      var summary = this.querySelector('[data-price-summary]');
      if (!summary) return;

      if (this.artworkType !== 'gallery' || !this.selectedPedal || !this.selectedArtwork) {
        summary.hidden = true;
        return;
      }

      summary.hidden = false;
      var feePrice = this.feeVariant ? this.feeVariant.price : 0;
      var feeFormatted = this.feeVariant ? this.feeVariant.priceFormatted : this.formatMoney(0);

      this.querySelector('[data-price-pedal-label]').textContent = this.selectedPedal.title;
      this.querySelector('[data-display-base-price]').textContent = this.selectedPedal.priceFormatted;
      this.querySelector('[data-display-fee-price]').textContent = feeFormatted;
      var sizeEl = this.querySelector('[data-display-size]');
      if (sizeEl) sizeEl.textContent = this.selectedPedal.size ? '(' + this.selectedPedal.size + ')' : '';
      this.querySelector('[data-display-total-price]').textContent =
        this.formatMoney(this.selectedPedal.price + feePrice);
    }

    // ---- Step 4 setup based on path ----

    prepareSubmitStep() {
      var contactDetails = this.querySelector('[data-contact-details]');
      var atcBtn = this.querySelector('[data-add-to-cart]');
      var submitBtn = this.querySelector('[data-submit]');

      if (this.artworkType === 'gallery') {
        // Hide contact details, show Add to Cart with live total
        if (contactDetails) contactDetails.style.display = 'none';
        this.setContactRequired(false);
        if (submitBtn) submitBtn.style.display = 'none';
        if (atcBtn) {
          atcBtn.style.display = '';
          var feePrice = this.feeVariant ? this.feeVariant.price : 0;
          var total = this.selectedPedal ? this.selectedPedal.price + feePrice : 0;
          atcBtn.querySelector('.button-label').textContent = 'Add to Cart — ' + this.formatMoney(total);
        }
      } else {
        // Custom path: show contact details + quote submit
        if (contactDetails) contactDetails.style.display = '';
        this.setContactRequired(true);
        if (atcBtn) atcBtn.style.display = 'none';
        if (submitBtn) submitBtn.style.display = '';
      }
    }

    setContactRequired(required) {
      this.querySelectorAll('[data-contact-required]').forEach(field => {
        if (required) {
          field.setAttribute('required', '');
        } else {
          field.removeAttribute('required');
        }
      });
    }

    // ---- Sidebar ----

    updateSidebar() {
      // Type
      var typeRow = this.querySelector('[data-sidebar-type]');
      if (this.pedalType) {
        typeRow.style.display = '';
        this.querySelector('[data-sidebar-type-value]').textContent =
          this.pedalType.charAt(0).toUpperCase() + this.pedalType.slice(1) + ' Pedal';
      } else {
        typeRow.style.display = 'none';
      }

      // Pedal
      var pedalRow = this.querySelector('[data-sidebar-pedal]');
      if (this.selectedPedal) {
        pedalRow.style.display = '';
        this.querySelector('[data-sidebar-pedal-thumb]').innerHTML =
          this.selectedPedal.image
            ? '<img src="' + this.selectedPedal.image + '" alt="' + this.escapeHtml(this.selectedPedal.title) + '" width="40" height="40">'
            : '';
        this.querySelector('[data-sidebar-pedal-name]').textContent = this.selectedPedal.title;
        this.querySelector('[data-sidebar-pedal-category]').textContent = this.selectedPedal.type || '';
        this.querySelector('[data-sidebar-pedal-size]').textContent =
          this.selectedPedal.size ? 'Size: ' + this.selectedPedal.size : '';
      } else {
        pedalRow.style.display = 'none';
      }

      // Artwork
      var artworkRow = this.querySelector('[data-sidebar-artwork]');
      var artworkImg = this.querySelector('[data-sidebar-artwork-img]');
      if (this.artworkType === 'gallery' && this.selectedArtwork) {
        artworkRow.style.display = '';
        artworkImg.innerHTML = '<img src="' + this.selectedArtwork.image + '" alt="' + this.escapeHtml(this.selectedArtwork.title) + '" loading="lazy">';
        this.querySelector('[data-sidebar-artwork-value]').textContent = this.selectedArtwork.title;
      } else if (this.artworkType === 'custom') {
        artworkRow.style.display = '';
        artworkImg.innerHTML = '';
        this.querySelector('[data-sidebar-artwork-value]').textContent = 'Custom design';
      } else {
        artworkRow.style.display = 'none';
      }

      // Pricing (gallery path)
      var pricing = this.querySelector('[data-sidebar-pricing]');
      if (this.artworkType === 'gallery' && this.selectedPedal && this.selectedArtwork) {
        pricing.style.display = '';
        var feePrice = this.feeVariant ? this.feeVariant.price : 0;
        var feeFormatted = this.feeVariant ? this.feeVariant.priceFormatted : this.formatMoney(0);
        this.querySelector('[data-sidebar-pedal-price]').textContent = this.selectedPedal.priceFormatted;
        this.querySelector('[data-sidebar-fee-price]').textContent = feeFormatted;
        this.querySelector('[data-sidebar-total-price]').textContent =
          this.formatMoney(this.selectedPedal.price + feePrice);
      } else {
        pricing.style.display = 'none';
      }
    }

    // ---- Gallery: Add to Cart ----

    async addToCart() {
      if (!this.selectedPedal || !this.selectedArtwork) return;
      if (!this.feeVariant || !this.feeVariant.id) {
        this.showError('Artwork fee is unavailable for this pedal. Please contact us.');
        return;
      }

      var atcBtn = this.querySelector('[data-add-to-cart]');
      var errorEl = this.querySelector('[data-error]');
      atcBtn.disabled = true;
      atcBtn.classList.add('loading');
      if (errorEl) errorEl.hidden = true;

      try {
        // Shared id linking the pedal line to its artwork-fee line, so the fee
        // can be auto-removed when the pedal is removed from the cart.
        var bundleRef = 'cab-' + Date.now() + '-' + Math.floor(Math.random() * 1e6);

        // Ask the add request to return fresh section HTML for the cart UI (the
        // count bubble + drawer), the same way the theme's product-form does —
        // so we don't need a second, error-prone fetch to refresh the cart.
        var cartComponent = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        var sectionIds = (cartComponent && typeof cartComponent.getSectionsToRender === 'function')
          ? cartComponent.getSectionsToRender().map(function (s) { return s.id; })
          : [];

        var body = {
          items: [
            { id: this.selectedPedal.variantId, quantity: 1, properties: { '_artwork_pedal_ref': bundleRef } },
            {
              id: this.feeVariant.id,
              quantity: 1,
              properties: {
                '_artwork_fee_ref': bundleRef,
                'Pedal': this.selectedPedal.title,
                'Pedal Size': this.selectedPedal.size || '',
                'Artwork': this.selectedArtwork.title,
                'Series': this.selectedArtwork.series || ''
              }
            }
          ]
        };

        // Step 4 preferences live in the contact form, which this path never
        // posts — copy them onto the artwork-fee line so they reach the order.
        var feeProps = body.items[1].properties;
        [
          ['controls_marked', 'Controls Marked'],
          ['model_names', 'Model Names'],
          ['knob_color', 'Knob Color/Type'],
          ['led_color', 'LED Color'],
          ['comments', 'Comments']
        ].forEach(([name, label]) => {
          var field = this.querySelector('[name="contact[' + name + ']"]');
          var value = field ? field.value.trim() : '';
          if (value) feeProps[label] = value;
        });
        if (sectionIds.length) {
          body.sections = sectionIds;
          body.sections_url = window.location.pathname;
        }

        var response = await fetch(this.config.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });

        var data = await response.json();
        if (data.status) {
          throw new Error(data.description || 'Could not add to cart');
        }

        // Notify the theme first so cart listeners (e.g. the artwork-fee
        // sweeper, and cart-drawer-items' own onCartUpdate) run against the
        // *old* drawer contents, then render the fresh sections last. Doing
        // it in the other order lets the freshly-mounted <cart-drawer-items>
        // (which subscribes to cartUpdate as soon as it's connected) receive
        // this event and immediately stomp the correct render with the main
        // cart page's markup.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'custom-artwork-builder',
            productVariantId: this.selectedPedal.variantId
          });
        }
        this.refreshCart(cartComponent, data);

        // Success screen
        var msgEl = this.querySelector('[data-success-cart-msg]');
        if (msgEl) {
          var feePrice = this.feeVariant ? this.feeVariant.price : 0;
          msgEl.textContent = this.selectedPedal.title + ' + ' + this.selectedArtwork.title +
            ' — ' + this.formatMoney(this.selectedPedal.price + feePrice);
        }
        this.goToSuccess('success-cart');
      } catch (error) {
        console.error('Add to cart error:', error);
        this.showError(error.message || 'Something went wrong. Please try again.');
        atcBtn.disabled = false;
      } finally {
        atcBtn.classList.remove('loading');
      }
    }

    // Re-render the theme's cart UI (count bubble + drawer) in place, using the
    // section HTML returned by the add request. Mirrors the theme's own render
    // loop but does NOT force the drawer open, so the builder's success screen
    // stays the primary confirmation.
    refreshCart(cartComponent, data) {
      if (!cartComponent || !data || !data.sections) return;
      if (cartComponent.classList.contains('is-empty')) {
        cartComponent.classList.remove('is-empty');
      }
      try {
        cartComponent.getSectionsToRender().forEach(function (section) {
          var html = data.sections[section.id];
          if (!html) return;
          var target = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!target) return;
          var source = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(section.selector || '.shopify-section');
          if (source) target.innerHTML = source.innerHTML;
        });
      } catch (e) {
        console.error('Cart refresh error:', e);
      }
    }

    // ---- Custom: Submit ----

    async submitForm(event) {
      event.preventDefault();
      if (this.artworkType !== 'custom') return;
      if (this.isSubmitting) return;

      // Validate step 4 required fields. Bring the first invalid field into view
      // and focus it before reporting — the native validation bubble alone can
      // land off-screen on this long step and read as "nothing happened".
      var step4 = this.querySelector('[data-step="4"]');
      var requiredFields = step4 ? step4.querySelectorAll('[required]') : [];
      for (var field of requiredFields) {
        if (field.offsetParent === null) continue; // skip hidden
        if (!field.checkValidity()) {
          field.scrollIntoView({ block: 'center', behavior: 'smooth' });
          field.focus({ preventScroll: true });
          field.reportValidity();
          return;
        }
      }

      // Populate hidden fields
      var pedal = this.selectedPedal || {};
      this.querySelector('[data-field-subject]').value = 'Custom Artwork Request — ' + (pedal.title || '');
      this.querySelector('[data-field-pedal]').value = pedal.title || '';
      this.querySelector('[data-field-pedal-url]').value = pedal.url ? ((this.config.rootUrl || '').replace(/\/$/, '') + pedal.url) : '';
      this.querySelector('[data-field-pedal-size]').value = pedal.size || '';
      this.querySelector('[data-field-artwork-type]').value = 'custom';

      var visionEl = this.querySelector('[data-custom-vision]');
      var refEl = this.querySelector('[data-custom-reference]');
      this.querySelector('[data-field-custom-vision]').value = visionEl ? visionEl.value : '';
      this.querySelector('[data-field-custom-reference]').value = refEl ? refEl.value : '';

      if (!this.form) {
        console.error('Custom artwork builder: no form found — cannot submit.');
        return;
      }

      this.isSubmitting = true;
      this.showError('');
      var submitBtn = this.querySelector('[data-submit]');
      submitBtn.disabled = true;
      submitBtn.classList.add('loading');

      // Hand the form to Shopify's CAPTCHA wrapper, which calls sendForm() once
      // the token is in place. If hCaptcha is still loading, wait for the ready
      // callback instead of firing a tokenless POST.
      if (this.captchaState === 'pending') {
        this.waitingForCaptcha = true;
      } else {
        this.form.submit();
      }

      // The wrapper stays silent when the token request fails, so don't leave
      // the button spinning forever.
      clearTimeout(this.submitWatchdog);
      this.submitWatchdog = setTimeout(
        () => this.failSubmit(new Error('CAPTCHA/submit timed out')),
        20000
      );
    }

    // Shopify's storefront CAPTCHA script (injected through content_for_header)
    // takes over every form that posts to /contact: when it binds the form it
    // captures the form's current submit function, then replaces form.submit
    // with its own, which fetches an hCaptcha token, appends it to the form as
    // the h-captcha-response input and finally calls the captured function.
    // A bare fetch() of the form skips that wrapper entirely, which is why
    // /contact answers 400 "Missing CAPTCHA token". Installing our AJAX sender
    // as form.submit *before* the wrapper binds (it binds on the first focusin
    // inside the form, so this has to happen during init) is what makes the
    // wrapper hand the token-carrying form back to us instead of navigating.
    setupCaptcha() {
      if (!this.form) return;

      // Captured by the wrapper on bind; also the direct sender on shops that
      // have CAPTCHA switched off, where nothing ever replaces form.submit.
      this.form.submit = () => this.sendForm();

      var captcha = window.Shopify && window.Shopify.captcha;
      if (!captcha || typeof captcha.protect !== 'function') return;

      // protect() binds the form up front and calls back once hCaptcha is ready.
      this.captchaState = 'pending';
      try {
        captcha.protect(this.form, () => {
          this.captchaState = 'ready';
          if (this.waitingForCaptcha) {
            this.waitingForCaptcha = false;
            this.form.submit();
          }
        });
      } catch (err) {
        console.error('Custom artwork builder: could not wire up CAPTCHA', err);
        this.captchaState = 'off';
      }
    }

    // Called by the CAPTCHA wrapper (see setupCaptcha) with the token already in
    // the form, or directly when the shop has no CAPTCHA.
    async sendForm() {
      if (!this.form || this.sendInFlight) return;
      this.sendInFlight = true;

      try {
        var response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          redirect: 'manual'
        });

        // An accepted contact post answers with a redirect back to the page,
        // which `redirect: 'manual'` surfaces as an opaque response (status 0).
        // Everything else — 400 missing/invalid token, 422, 429 — is a real
        // failure and must not be dressed up as a success.
        if (!response.ok && response.type !== 'opaqueredirect' && response.status !== 0) {
          throw new Error('Contact form responded ' + response.status);
        }

        clearTimeout(this.submitWatchdog);
        this.showError('');

        var btn = this.querySelector('[data-submit]');
        if (btn) btn.classList.remove('loading');

        var pedalTitle = (this.selectedPedal || {}).title;
        var msgEl = this.querySelector('[data-success-quote-msg]');
        if (msgEl && pedalTitle) {
          msgEl.textContent = 'Our team will review your custom artwork request for ' +
            pedalTitle + ' and get back to you with a quote.';
        }
        this.goToSuccess('success-quote');
      } catch (error) {
        this.failSubmit(error);
      } finally {
        this.sendInFlight = false;
      }
    }

    failSubmit(error) {
      clearTimeout(this.submitWatchdog);
      if (!this.isSubmitting) return;

      console.error('Form submission error:', error);
      this.isSubmitting = false;
      this.waitingForCaptcha = false;

      var submitBtn = this.querySelector('[data-submit]');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('loading');
      }

      this.showError(
        "We couldn't send your request. Please try again — if it keeps failing, email us and we'll pick it up from there."
      );
    }

    // ---- Image lightbox ----

    buildLightbox() {
      var lb = document.createElement('div');
      lb.className = 'cab__lightbox';
      lb.setAttribute('hidden', '');
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.innerHTML =
        '<button type="button" class="cab__lightbox-close" data-lightbox-close aria-label="Close">' +
          '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<figure class="cab__lightbox-fig">' +
          '<img class="cab__lightbox-img" data-lightbox-img src="" alt="">' +
          '<figcaption class="cab__lightbox-caption" data-lightbox-caption></figcaption>' +
        '</figure>';
      document.body.appendChild(lb);
      this.lightbox = lb;

      lb.addEventListener('click', (e) => {
        if (e.target === lb || e.target.closest('[data-lightbox-close]')) {
          this.closeLightbox();
        }
      });
      this._lightboxKeydown = (e) => {
        if (e.key === 'Escape') this.closeLightbox();
      };
    }

    openLightbox(src, title) {
      if (!this.lightbox || !src) return;
      var img = this.lightbox.querySelector('[data-lightbox-img]');
      var cap = this.lightbox.querySelector('[data-lightbox-caption]');
      img.src = src;
      img.alt = title || '';
      if (cap) {
        cap.textContent = title || '';
        cap.style.display = title ? '' : 'none';
      }
      this.lightbox.removeAttribute('hidden');
      document.body.classList.add('cab-lightbox-open');
      document.addEventListener('keydown', this._lightboxKeydown);
      var closeBtn = this.lightbox.querySelector('[data-lightbox-close]');
      if (closeBtn) closeBtn.focus();
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.setAttribute('hidden', '');
      document.body.classList.remove('cab-lightbox-open');
      document.removeEventListener('keydown', this._lightboxKeydown);
      var img = this.lightbox.querySelector('[data-lightbox-img]');
      if (img) img.src = '';
    }

    // ---- Helpers ----

    goToSuccess(stepName) {
      var sidebar = this.querySelector('[data-sidebar]');
      if (sidebar) sidebar.style.display = 'none';
      var indicator = this.querySelector('.cab__steps-indicator');
      if (indicator) indicator.style.display = 'none';
      var stepLabel = this.querySelector('[data-step-label]');
      if (stepLabel) stepLabel.style.display = 'none';
      this.showStep(stepName);
    }

    showError(message) {
      var errorEl = this.querySelector('[data-error]');
      if (errorEl) {
        errorEl.textContent = message || '';
        errorEl.hidden = !message;
      }
    }

    escapeHtml(str) {
      return String(str || '').replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    formatMoney(cents) {
      var format = this.config.moneyFormat;
      var amount = (cents / 100).toFixed(2);
      return format
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.floor(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(
          /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
          Math.floor(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
        );
    }
  }

  customElements.define('custom-artwork-builder', CustomArtworkBuilder);
}
